

import { useState, useMemo, useEffect, useRef } from "react"
import axios from "axios"
import "katex/dist/katex.min.css"
import React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import katex from "katex"
import QuestionFetcher from "./QuestionFetcher"
import QuizFormatter from "./QuizFormatter"

// Create a context to share search state across components
export const SearchStateContext = React.createContext(null)

// Store search state in session storage
const saveSearchState = (state) => {
  try {
    sessionStorage.setItem("questionSearchState", JSON.stringify(state))
  } catch (error) {
    console.error("Failed to save search state:", error)
  }
}

// Load search state from session storage
const loadSearchState = () => {
  try {
    const savedState = sessionStorage.getItem("questionSearchState")
    return savedState ? JSON.parse(savedState) : null
  } catch (error) {
    console.error("Failed to load search state:", error)
    return null
  }
}

const API_URL = "https://math-image-transcriber-steveszren.replit.app/api/convert"

async function fetchQuestionData(searchText, setResults) {
  try {
    if (searchText) {
      const response = await axios.post("/api/queryQuestionBank", {
        searchText,
      })
      setResults(response.data)
      return response.data // Return the results for further processing
    }
    return []
  } catch (error) {
    console.error("Request failed: ", error)
    setResults([])
    return []
  }
}

function FilterableQuestionTable({
  onQuestionSelected,
  searchText,
  setSearchText,
  results,
  setResults,
  currentQuestions,
  setCurrentQuestions,
  selectedFilter,
  setSelectedFilter,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  selectedButton,
  setSelectedButton,
  selectedQuestions,
  setSelectedQuestions,
  includeSolution,
  setIncludeSolution,
  showRandomModal,
  setShowRandomModal,
  showFilterDropdown,
  setShowFilterDropdown,
  loading,
  setLoading,
  error,
  setError,
}) {
  useEffect(() => {
    const calculateItemsPerPage = () => {
      const viewportHeight = window.innerHeight
      const headerHeight = 64 // 16 * 4 = 64px
      const searchSectionHeight = 200 // Approximate height for search section
      const questionCardHeight = 160 // Approximate height for each question card
      const availableHeight = viewportHeight - headerHeight - searchSectionHeight
      return Math.max(3, Math.floor(availableHeight / questionCardHeight))
    }
    setItemsPerPage(calculateItemsPerPage())
    window.addEventListener("resize", () => setItemsPerPage(calculateItemsPerPage()))
  }, [])

  const statesFunctions = {
    searchText,
    setSearchText,
    selectedFilter,
    setSelectedFilter,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    onQuestionSelected,
    results,
    setResults,
    currentQuestions,
    setCurrentQuestions,
    selectedButton,
    setSelectedButton,
    selectedQuestions,
    setSelectedQuestions,
    includeSolution,
    setIncludeSolution,
    showRandomModal,
    setShowRandomModal,
    showFilterDropdown,
    setShowFilterDropdown,
    loading,
    setLoading,
    error,
    setError,
  }
  return (
    <div className="min-h-screen bg-[#ebebeb]" id="original-content">
      <div className="max-w-[1440px] mx-auto px-8 py-8 ">
        <SearchBar className="w-full md:w-[400px]" {...statesFunctions} />
        <div className="flex gap-8 mt-5">
          <QuestionTable className="w-full md:w-1/2" {...statesFunctions} />
          <FilterTable className="w-full md:w-1/2" {...statesFunctions} />
        </div>
      </div>
    </div>
  )
}

function UploadImage({ searchText, setSearchText, setSelectedImage, setIsImgLoading }) {
  const fileInputRef = useRef(null)
  const handleImageUpload = () => {
    const file = fileInputRef.current.files[0]
    if (file) {
      const reader = new FileReader()
      setIsImgLoading(true)
      reader.onload = async (event) => {
        setSelectedImage(reader.result)
        const formData = new FormData()
        formData.append("image", file)
        try {
          const response = await axios.post(process.env.REACT_APP_IMG_RECOGNIZE_API, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            withCredentials: false,
          })

          const data = response.data
          if (data.success) {
            setSearchText(data.result.trim())
          } else {
            alert(data.error || "Failed to convert image")
          }
        } catch (err) {
          alert("Error uploading image")
        } finally {
          setIsImgLoading(false)
        }
      }

      reader.readAsDataURL(file)
    }
  }

  return (
    <div>
      <input type="file" ref={fileInputRef} className="hidden" onChange={handleImageUpload} />
      <button
        className="flex items-center justify-center hover:bg-gray-200 rounded-lg px-4 py-2 ml-4 !rounded-button whitespace-nowrap text-gray-500"
        onClick={() => fileInputRef.current.click()}
      >
        <i className="fas fa-image"></i>
      </button>
    </div>
  )
}

function SearchBar({ searchText, setSearchText, setResults, loading, setLoading, setError, selectedFilter }) {
  const [selectedImage, setSelectedImage] = useState(null)
  const [isLargeImageVisible, setIsLargeImageVisible] = useState(false)
  const [isImgLoading, setIsImgLoading] = useState(false)

  const handlePaste = async (e) => {
    const items = (e.clipboardData || window.clipboardData).items
    let isImagePasted = false
    for (const item of items) {
      if (item.type.indexOf("image") !== -1) {
        isImagePasted = true
        const blob = item.getAsFile()
        if (blob) {
          const reader = new FileReader()
          setIsImgLoading(true)
          reader.onload = async (event) => {
            setSelectedImage(reader.result)
            const formData = new FormData()
            formData.append("image", blob)
            try {
              const response = await axios.post(process.env.REACT_APP_IMG_RECOGNIZE_API, formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
                withCredentials: false,
              })

              const data = response.data
              if (data.success) {
                setSearchText(data.result.trim())
              } else {
                alert(data.error || "Failed to convert image")
              }
            } catch (err) {
              alert("Error uploading image")
            } finally {
              setIsImgLoading(false)
            }
          }
          reader.readAsDataURL(blob)
        }
      }
    }
    if (!isImagePasted) {
      return
    }
    e.preventDefault()
  }

  return (
    <div className="relative">
      <div className="flex items-center justify-center bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-center w-full">
          <i className="fas fa-search text-gray-400 text-lg mr-3"></i>
          <input
            type="text"
            placeholder="Enter your question description..."
            className="flex-1 text-base border-none outline-none focus:ring-0"
            value={searchText}
            onChange={(e) => {
              const newText = e.target.value
              setSearchText(newText)

              // Debounce the search to avoid too many API calls
              if (newText.length >= 2) {
                clearTimeout(window.searchDebounceTimer)
                window.searchDebounceTimer = setTimeout(async () => {
                  setLoading(true)
                  setError(null) // Clear any previous errors
                  try {
                    // Use the new search with fallback method if filters are applied
                    const hasFilters = selectedFilter && Object.keys(selectedFilter).length > 0

                    if (hasFilters) {
                      const searchResult = await QuestionFetcher.searchWithFallback(newText, selectedFilter)
                      setResults(searchResult.questions)

                      if (searchResult.fallbackUsed) {
                        console.log("Filters applied but no results found. Showing unfiltered results.")
                      }

                      if (searchResult.questions.length === 0) {
                        setError(`No questions found for "${newText}". Try different keywords or broader terms.`)
                      }
                    } else {
                      // Use regular search if no filters
                      const searchResults = await QuestionFetcher.fetchQuestionData(newText, setResults)
                      if (searchResults.length === 0) {
                        setError(`No questions found for "${newText}". Try different keywords or broader terms.`)
                      }
                    }
                  } catch (error) {
                    console.error("Search failed:", error)
                    setError("Search failed. Please check your connection and try again.")
                  }
                }, 500) // Wait 500ms after typing stops
              } else if (newText.length === 0) {
                // Clear results when search is empty
                setResults([])
                setError(null)
              }
            }}
            onPaste={handlePaste}
          />

          {isImgLoading ? (
            <div className="relative border border-gray-100 rounded-lg w-8 h-8 flex items-center justify-center">
              <i className="fas fa-spinner fa-spin text-gray-400"></i>
            </div>
          ) : (
            selectedImage && (
              <div className="relative border border-gray-100 rounded-lg">
                <img
                  src={selectedImage || "/placeholder.svg"}
                  alt="Preview"
                  className="w-8 h-8 object-cover rounded-lg"
                  onClick={() => setIsLargeImageVisible(true)}
                />
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-600 text-white flex items-center justify-center text-xs hover:bg-red-700"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )
          )}
          <UploadImage
            searchText={searchText}
            setSearchText={setSearchText}
            setSelectedImage={setSelectedImage}
            setIsImgLoading={setIsImgLoading}
          />
          <button
            onClick={async () => {
              if (!searchText) {
                setError("Please enter search terms")
                return
              }
              setLoading(true)
              setError(null)
              try {
                // Use the new search with fallback method if filters are applied
                const hasFilters = selectedFilter && Object.keys(selectedFilter).length > 0

                if (hasFilters) {
                  const searchResult = await QuestionFetcher.searchWithFallback(searchText, selectedFilter)
                  setResults(searchResult.questions)

                  if (searchResult.fallbackUsed) {
                    console.log("Filters applied but no results found. Showing unfiltered results.")
                    // Optionally show a message to the user
                    setError(
                      `No questions found with current filters for "${searchText}". Showing all search results instead.`,
                    )
                  }

                  if (searchResult.questions.length === 0) {
                    setError(`No questions found for "${searchText}". Try different keywords or broader terms.`)
                  }
                } else {
                  // Use regular search if no filters
                  const searchResults = await QuestionFetcher.fetchQuestionData(searchText, setResults)
                  if (searchResults.length === 0) {
                    setError(`No questions found for "${searchText}". Try different keywords or broader terms.`)
                  }
                }
              } catch (error) {
                console.error("Search failed:", error)
                setError("Search failed. Please check your connection and try again.")
              }
              // Don't set loading to false here - let the QuestionTable component handle it
            }}
            disabled={loading}
            className={`text-gray-600 hover:text-gray-800 ${
              loading ? "bg-gray-200 text-gray-400" : "bg-red-600 text-white hover:bg-red-700"
            } w-6 h-6 rounded-full flex items-center justify-center !rounded-button`}
          >
            {loading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-arrow-up"></i>}
          </button>
        </div>
      </div>
      {isLargeImageVisible && selectedImage && (
        <div
          className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setIsLargeImageVisible(false)}
        >
          <div className="relative max-w-4xl mx-auto">
            <img src={selectedImage || "/placeholder.svg"} alt="Large Preview" className="max-w-full max-h-full" />
            <button
              onClick={() => setIsLargeImageVisible(false)}
              className="absolute top-[-40px] right-[-40px] w-8 h-8 text-white flex items-center justify-center text-xl hover:bg-gray-500"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function QuestionTable({
  searchText,
  selectedFilter,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  onQuestionSelected,
  results,
  setResults,
  currentQuestions,
  setCurrentQuestions,
  selectedQuestions,
  setSelectedQuestions,
  includeSolution,
  setIncludeSolution,
  showRandomModal,
  setShowRandomModal,
  loading,
  setLoading,
  error,
  setError,
}) {
  // Reference to track if questions are rendered
  const questionsContainerRef = useRef(null)
  const [questionsRendered, setQuestionsRendered] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true) // Start loading
      setError(null) // Clear previous error messages when starting a new search
      try {
        await QuestionFetcher.fetchQuestionData(searchText, setResults)
      } catch (error) {
        console.error("Search failed:", error)
        setError("Search failed. Please try again later.") // Set error message
      }
      // Keep loading true until questions are rendered
    }

    // Only fetch on mount or when explicitly triggered by search button
    // The input onChange handler will handle automatic searching
    if (searchText && results.length === 0) {
      fetchData()
    }
  }, []) // Remove searchText dependency to prevent double fetching

  const filteredQuestionsMemo = useMemo(() => {
    let filtered = results

    // If we have filters applied, try to filter the results
    if (selectedFilter && Object.keys(selectedFilter).length > 0) {
      const tempFiltered = results.filter((q) => {
        return Object.entries(selectedFilter).every(([key, value]) => {
          if (!value || value === "All") return true
          return q[key] === value
        })
      })

      // If filtering results in no questions, fall back to unfiltered results
      if (tempFiltered.length === 0) {
        console.log("No results found with current filters, showing all search results")
        filtered = results
      } else {
        filtered = tempFiltered
      }
    }

    return filtered
  }, [results, selectedFilter])

  const currentQuestionsMemo = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return filteredQuestionsMemo.slice(startIndex, startIndex + itemsPerPage)
  }, [filteredQuestionsMemo, currentPage, itemsPerPage])

  useEffect(() => {
    setCurrentQuestions(currentQuestionsMemo)
  }, [currentQuestionsMemo, setCurrentQuestions])

  // Use a separate effect to check if questions are rendered
  useEffect(() => {
    // If we have questions to show
    if (currentQuestionsMemo.length > 0) {
      // Use a mutation observer to detect when the questions are actually rendered in the DOM
      const observer = new MutationObserver((mutations) => {
        const questionElements = questionsContainerRef.current?.querySelectorAll(".bg-white.rounded-lg")
        if (questionElements && questionElements.length > 0) {
          setQuestionsRendered(true)
        }
      })

      if (questionsContainerRef.current) {
        observer.observe(questionsContainerRef.current, {
          childList: true,
          subtree: true,
        })
      }

      return () => {
        observer.disconnect()
      }
    } else if (results.length === 0 && searchText && loading) {
      // If search is complete but no results found
      setTimeout(() => {
        setQuestionsRendered(true)
      }, 500)
    }
  }, [currentQuestionsMemo, results.length, searchText, loading])

  // Effect to handle loading state based on questions being rendered
  useEffect(() => {
    if (loading && questionsRendered) {
      // Add a small delay to ensure smooth transition
      setTimeout(() => {
        setLoading(false)
        setQuestionsRendered(false) // Reset for next search
      }, 300)
    }

    // Add this section to handle the case when we have results but loading is still true
    // This serves as a fallback in case the mutation observer doesn't detect rendered questions
    if (loading && currentQuestionsMemo.length > 0 && !questionsRendered) {
      // If we have questions but they haven't been marked as rendered after a timeout
      const timer = setTimeout(() => {
        console.log("Fallback: Setting loading to false after timeout")
        setLoading(false)
      }, 1500) // Longer timeout as fallback

      return () => clearTimeout(timer)
    }
  }, [questionsRendered, loading, setLoading, currentQuestionsMemo.length])

  // Make sure currentQuestions is always updated when results change
  useEffect(() => {
    if (results.length > 0) {
      const startIndex = (currentPage - 1) * itemsPerPage
      const filtered = selectedFilter
        ? results.filter((q) => {
            for (const [key, value] of Object.entries(selectedFilter)) {
              if (value && q[key] !== value) return false
            }
            return true
          })
        : results

      setCurrentQuestions(filtered.slice(startIndex, startIndex + itemsPerPage))

      // If we have results but no questions showing, it might be because of pagination
      if (filtered.length > 0 && currentPage > Math.ceil(filtered.length / itemsPerPage)) {
        setCurrentPage(1) // Reset to first page if current page is invalid
      }
    }
  }, [results, selectedFilter, currentPage, itemsPerPage])

  const totalPages = Math.ceil(filteredQuestionsMemo.length / itemsPerPage)
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <div className="flex-1 bg-[#fafafa] rounded">
      {loading && (
        <div className="flex items-center justify-center h-64 w-full">
          <i className="fas fa-spinner fa-spin text-3xl text-gray-400"></i>
          <span className="ml-3 text-gray-500">Loading questions...</span>
        </div>
      )}
      {!loading && (
        <>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <div className="flex items-center justify-between mb-6 pl-8 pr-8 mt-4">
            <label className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 text-[#b82322] rounded border-gray-300 focus:ring-0"
                checked={selectedQuestions.length === currentQuestions.length && currentQuestions.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedQuestions(currentQuestions.map((q) => q.id))
                  } else {
                    setSelectedQuestions([])
                  }
                }}
              />
              Select All Questions <span className="text-[#b82322] ml-2">({selectedQuestions.length} Selected)</span>
            </label>
            <div className="flex ml-auto items-end space-x-1">
              <h2 className="text-2xl font-bold">Search Results</h2>
              <span className="text-gray-500">({filteredQuestionsMemo.length} question(s) found)</span>
              {selectedFilter && Object.keys(selectedFilter).length > 0 && (
                <span className="text-blue-600 text-sm ml-2">
                  (Filters applied: {Object.keys(selectedFilter).length})
                </span>
              )}
            </div>
            <div className="relative flex items-center space-x-1">
              <button
                className="flex items-center px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none hover:border-blue-500 bg-gray-100 text-black"
                onClick={(e) => {
                  const target = e.currentTarget.nextElementSibling
                  const icon = e.currentTarget.querySelector("i")
                  if (target.style.display === "none") {
                    target.style.display = "block"
                    icon.classList.remove("fa-chevron-down")
                    icon.classList.add("fa-chevron-up")
                  } else {
                    target.style.display = "none"
                    icon.classList.remove("fa-chevron-up")
                    icon.classList.add("fa-chevron-down")
                  }
                }}
              >
                {itemsPerPage}
                <i className="fas fa-chevron-down ml-2 text-gray-400"></i>
              </button>
              <div className="absolute top-full left-0 mt-0 w-full bg-white border border-gray-300 rounded shadow-lg hidden">
                {[3, 5, 10, 15, 20].map((num) => (
                  <button
                    key={num}
                    className="block w-full text-left px-3 py-2 hover:bg-gray-50 text-sm bg-white text-black space-y-0 m-0"
                    onClick={() => {
                      setItemsPerPage(num)
                      setCurrentPage(1)
                      const parent = document.activeElement?.parentElement
                      const button = parent?.previousElementSibling
                      const icon = button?.querySelector("i")
                      if (parent) parent.style.display = "none"
                      if (icon) {
                        icon.classList.remove("fa-chevron-up")
                        icon.classList.add("fa-chevron-down")
                      }
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <span className="text-sm text-gray-500">per page</span>
            </div>
          </div>

          <div ref={questionsContainerRef} className="space-y-4 pl-8 pr-8">
            {currentQuestions.length > 0 ? (
              currentQuestions.map((question) => (
                <QuestionRow
                  question={question}
                  key={question.id}
                  onQuestionSelected={onQuestionSelected}
                  selectedQuestions={selectedQuestions}
                  setSelectedQuestions={setSelectedQuestions}
                />
              ))
            ) : (
              <div className="text-center py-10 text-gray-500">
                {searchText
                  ? "No questions found matching your search criteria."
                  : "Enter a search term to find questions."}
              </div>
            )}
          </div>

          {currentQuestions.length > 0 && (
            <div className="flex mt-8 pl-8 pr-8 mb-8">
              <PaginationButtons
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
              />
              <GenerateQuiz
                selectedQuestions={selectedQuestions}
                setSelectedQuestions={setSelectedQuestions}
                currentQuestions={currentQuestions}
                includeSolution={includeSolution}
                setIncludeSolution={setIncludeSolution}
                showRandomModal={showRandomModal}
                setShowRandomModal={setShowRandomModal}
                results={results}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

function PaginationButtons({ currentPage, totalPages, handlePageChange }) {
  const buttons = []

  // Always show first page button
  buttons.push(
    <button
      key="first"
      onClick={() => handlePageChange(1)}
      className={`px-3 py-1 mx-1 rounded whitespace-nowrap text-sm ${
        currentPage === 1 ? "bg-[#c73231] text-white" : "bg-[#e5e7eb] text-gray-700 hover:bg-[#d1d5db]"
      }`}
      disabled={currentPage === 1}
    >
      <i className="fas fa-angle-double-left"></i>
    </button>,
  )

  // Previous page button
  buttons.push(
    <button
      key="prev"
      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
      className="px-3 py-1 mx-1 rounded whitespace-nowrap text-sm bg-[#e5e7eb] text-gray-700 hover:bg-[#d1d5db]"
      disabled={currentPage === 1}
    >
      <i className="fas fa-angle-left"></i>
    </button>,
  )

  // Calculate which page buttons to show
  const maxVisiblePages = 3 // Reduced from 5 to make buttons more compact
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
  const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

  // Adjust start page if we're near the end
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1)
  }

  // Add ellipsis if needed before the visible pages
  if (startPage > 1) {
    buttons.push(
      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
        ...
      </span>,
    )
  }

  // Add the visible page buttons
  for (let i = startPage; i <= endPage; i++) {
    buttons.push(
      <button
        key={i}
        onClick={() => handlePageChange(i)}
        className={`px-3 py-1 mx-1 rounded whitespace-nowrap text-sm ${
          currentPage === i ? "bg-[#c73231] text-white" : "bg-[#e5e7eb] text-gray-700 hover:bg-[#d1d5db]"
        }`}
      >
        {i}
      </button>,
    )
  }

  // Add ellipsis if needed after the visible pages
  if (endPage < totalPages) {
    buttons.push(
      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
        ...
      </span>,
    )
  }

  // Next page button
  buttons.push(
    <button
      key="next"
      onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
      className="px-3 py-1 mx-1 rounded whitespace-nowrap text-sm bg-[#e5e7eb] text-gray-700 hover:bg-[#d1d5db]"
      disabled={currentPage === totalPages}
    >
      <i className="fas fa-angle-right"></i>
    </button>,
  )

  // Always show last page button
  buttons.push(
    <button
      key="last"
      onClick={() => handlePageChange(totalPages)}
      className={`px-3 py-1 mx-1 rounded whitespace-nowrap text-sm ${
        currentPage === totalPages ? "bg-[#c73231] text-white" : "bg-[#e5e7eb] text-gray-700 hover:bg-[#d1d5db]"
      }`}
      disabled={currentPage === totalPages}
    >
      <i className="fas fa-angle-double-right"></i>
    </button>,
  )

  return buttons
}

// Replace the current MathFormulaParse function with this improved version
function MathFormulaParse(content) {
  // Handle null, undefined, or non-string content
  if (!content) return ""

  // If content is an object, try to extract text or stringify it
  if (typeof content === "object") {
    // Check if it's a solution object with Parts property
    if (content.Parts && Array.isArray(content.Parts)) {
      // Join all parts of the solution
      const joinedContent = content.Parts.map((part) => {
        // Handle marking scheme that might be an object
        let markingScheme = part.MarkingScheme
        if (markingScheme && typeof markingScheme === "object") {
          try {
            // Try to convert to JSON string for display
            markingScheme = JSON.stringify(markingScheme, null, 2)
          } catch (e) {
            markingScheme = "See detailed marking scheme in solution"
          }
        }

        return [part.Solution, markingScheme ? `\n Marking Scheme: ${markingScheme} \n` : ""].filter(Boolean).join("\n")
      }).join("\n")

      // Now process the joined content
      return MathFormulaParse(joinedContent)
    }

    // If it's some other object, try to stringify it
    try {
      content = JSON.stringify(content, null, 2)
    } catch (e) {
      console.error("Failed to stringify content:", e)
      return "Error: Could not process content"
    }
  }

  // Now we're sure content is a string, proceed with normal processing
  // Pre-process LaTeX delimiters for consistency
  content = content.replace(/\\\[\s*/g, "\\[")
  content = content.replace(/\s*\\\]/g, "\\]")
  content = content.replace(/\\\(\s*/g, "\\(")
  content = content.replace(/\\\\\\\(\s*/g, "\\(\\\\")
  content = content.replace(/\s*\\\)/g, "\\)")

  // Process the content all at once for better reliability
  const regex =
    /(\\$[\s\S]*?\\$)|(\\$$[\s\S]*?\\$$)|(\\\[[\s\S]*?\\\])|(\$[\s\S]*?\$)|(\$\$[\s\S]*?\$\$)|(\\begin\{[\s\S]*?\\end\{[\s\S]*?\})|(\\textit\{.*?\})|(\\textbf\{.*?\})/g

  let displayContent = ""
  let lastIndex = 0
  let match

  while ((match = regex.exec(content)) !== null) {
    // Add text before the LaTeX
    if (match.index > lastIndex) {
      displayContent += content.substring(lastIndex, match.index).replace(/\n/g, "<br />")
    }

    const part = match[0]
    try {
      const isDisplayMode = part.startsWith("\\[") || part.startsWith("$$")

      // Extract the math content based on the delimiter type
      let mathContent
      if (part.startsWith("$$") && part.endsWith("$$")) {
        mathContent = part.substring(2, part.length - 2)
      } else if (part.startsWith("\\[") && part.endsWith("\\]")) {
        mathContent = part.substring(2, part.length - 2)
      } else if (part.startsWith("$$") && part.endsWith("$$")) {
        mathContent = part.substring(2, part.length - 2)
      } else if (part.startsWith("$") && part.endsWith("$") && !part.startsWith("$$")) {
        mathContent = part.substring(1, part.length - 1)
      } else if (part.startsWith("\\begin{")) {
        mathContent = part
      } else {
        mathContent = part
      }

      const renderedMath = katex.renderToString(mathContent, {
        throwOnError: false,
        displayMode: isDisplayMode,
        output: "html",
      })

      displayContent += renderedMath
    } catch (e) {
      console.error("Error rendering LaTeX:", e, part)
      // Fallback to showing the raw LaTeX
      displayContent += part
    }

    lastIndex = match.index + part.length
  }

  // Add remaining text
  if (lastIndex < content.length) {
    displayContent += content.substring(lastIndex).replace(/\n/g, "<br />")
  }

  return displayContent
}

// Modify the GenerateQuiz function to use random questions
function GenerateQuiz({
  selectedQuestions,
  currentQuestions,
  includeSolution,
  setIncludeSolution,
  showRandomModal,
  setShowRandomModal,
  results,
}) {
  const handleGenerateQuiz = () => {
    // Get all selected questions from the full results array, not just current page
    const allSelectedQuestions = results.filter((question) => selectedQuestions.includes(question.id))

    // Randomize the order of questions
    const randomizedQuestions = [...allSelectedQuestions].sort(() => 0.5 - Math.random())

    // Use the updated QuizFormatter with proper options
    QuizFormatter.printFormattedQuiz(randomizedQuestions, {
      includeSolution: includeSolution,
      format: QuizFormatter.QUIZ_FORMATS.QUIZ,
    })
  }

  return (
    <div className="flex items-center gap-4 ml-auto">
      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
          onChange={(e) => setIncludeSolution(e.target.checked)}
        />
        Include Solution
      </label>
      <button
        onClick={handleGenerateQuiz}
        disabled={selectedQuestions.length === 0}
        className={`px-6 py-2 rounded-lg transition-colors !rounded-button whitespace-nowrap ${
          selectedQuestions.length > 0
            ? "bg-[#b82322] text-white hover:bg-[#901c1b] cursor-pointer"
            : "bg-[#d1d5db] text-gray-500 cursor-not-allowed"
        }`}
      >
        <i className="fas fa-file-alt mr-2"></i>
        Generate Quiz
      </button>
      <button
        onClick={() => setShowRandomModal(true)}
        className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 !rounded-button whitespace-nowrap ${
          results.length > 0
            ? "bg-[#b82322] text-white hover:bg-[#901c1b] cursor-pointer"
            : "bg-[#d1d5db] text-gray-500 cursor-not-allowed"
        }`}
      >
        <i className="fas fa-random"></i>
        Random Quiz
      </button>
      <RandomQuizModal
        isOpen={showRandomModal}
        onClose={() => setShowRandomModal(false)}
        questions={results}
        onGenerate={(count, includeSolution) => {
          // Randomly select the specified number of questions from ALL results
          const shuffled = [...results].sort(() => 0.5 - Math.random())
          const selectedRandomQuestions = shuffled.slice(0, count)
          QuizFormatter.printFormattedQuiz(selectedRandomQuestions, {
            includeSolution: includeSolution,
            format: QuizFormatter.QUIZ_FORMATS.QUIZ,
          })
        }}
      />
    </div>
  )
}

// Update the PrintQuestions function to remove QuizSetupWizard elements and use full question content

// Update the RandomQuizModal to use the full results array
function RandomQuizModal({ isOpen, onClose, onGenerate, questions }) {
  const [questionCount, setQuestionCount] = useState(10)
  const [includeSolution, setIncludeSolution] = useState(false)
  const [quizFormat, setQuizFormat] = useState(QuizFormatter.QUIZ_FORMATS.QUIZ)

  const handleRandomQuiz = () => {
    // Randomly select the specified number of questions from ALL results
    const shuffled = [...questions].sort(() => 0.5 - Math.random())
    const selectedRandomQuestions = shuffled.slice(0, questionCount)

    // Call onGenerate to notify parent component
    onGenerate(questionCount, includeSolution)

    // Generate the quiz with the selected questions
    QuizFormatter.printFormattedQuiz(selectedRandomQuestions, {
      includeSolution: includeSolution,
      format: quizFormat,
    })
  }

  if (!isOpen) return null
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[400px]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Generate Random Quiz</h2>
          <button onClick={onClose} className="text-gray-500 hover:bg-gray-100 bg-white">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Number of Questions</label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                value={questionCount}
                onChange={(e) =>
                  setQuestionCount(Math.max(1, Math.min(questions.length, Number.parseInt(e.target.value) || 1)))
                }
                className="w-24 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1"
                max={questions.length}
              />
              <div className="flex gap-2">
                {[5, 10, 15, 20].map((num) => (
                  <button
                    key={num}
                    onClick={() => setQuestionCount(Math.min(num, questions.length))}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      questionCount === num ? "bg-blue-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
                    } !rounded-button whitespace-nowrap`}
                    style={{ border: "1px solid #ccc", borderStyle: "solid" }}
                    disabled={num > questions.length}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
            {questions.length < 20 && (
              <p className="text-xs text-gray-500 mt-1">Maximum {questions.length} questions available</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quiz Format</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setQuizFormat(QuizFormatter.QUIZ_FORMATS.QUIZ)}
                className={`px-3 py-2 rounded-lg text-sm ${
                  quizFormat === QuizFormatter.QUIZ_FORMATS.QUIZ
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                } !rounded-button whitespace-nowrap`}
                style={{ border: "1px solid #ccc", borderStyle: "solid" }}
              >
                Quiz
              </button>
              <button
                onClick={() => setQuizFormat(QuizFormatter.QUIZ_FORMATS.PRACTICE)}
                className={`px-3 py-2 rounded-lg text-sm ${
                  quizFormat === QuizFormatter.QUIZ_FORMATS.PRACTICE
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                } !rounded-button whitespace-nowrap`}
                style={{ border: "1px solid #ccc", borderStyle: "solid" }}
              >
                Practice
              </button>
              <button
                onClick={() => setQuizFormat(QuizFormatter.QUIZ_FORMATS.EXAM)}
                className={`px-3 py-2 rounded-lg text-sm ${
                  quizFormat === QuizFormatter.QUIZ_FORMATS.EXAM
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                } !rounded-button whitespace-nowrap`}
                style={{ border: "1px solid #ccc", borderStyle: "solid" }}
              >
                Exam
              </button>
            </div>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeSolution}
                onChange={(e) => setIncludeSolution(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-[#999999]">Include Solution</span>
            </label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-50 !rounded-button whitespace-nowrap bg-white"
            style={{ border: "1px solid #ccc", borderStyle: "solid" }}
          >
            Cancel
          </button>
          <button
            onClick={handleRandomQuiz}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 !rounded-button whitespace-nowrap flex items-center justify-center min-w-[100px]"
          >
            Generate
          </button>
        </div>
      </div>
    </div>
  )
}

function QuestionRow({ question, onQuestionSelected, selectedQuestions, setSelectedQuestions }) {
  const navigate = useNavigate()

  const handleQuestionClick = () => {
    // Save current search state before navigating
    const searchState = {
      searchText: document.querySelector('input[placeholder="Enter your question description..."]')?.value || "",
      results: window.currentSearchResults || [],
      currentQuestions: window.currentQuestions || [],
      selectedFilter: window.selectedFilter || {},
      currentPage: window.currentPage || 1,
      itemsPerPage: window.itemsPerPage || 5,
      selectedQuestions: window.selectedQuestions || [],
      includeSolution: window.includeSolution || false,
    }

    // Save to session storage
    saveSearchState(searchState)

    navigate(`/exam-question/${question.id}`)
  }

  const handleCheckQuestion = (id) => {
    setSelectedQuestions((prev) => {
      if (prev.includes(id)) {
        return prev.filter((qId) => qId !== id)
      }
      return [...prev, id]
    })
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-4 flex items-center justify-between">
      <label className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 cursor-pointer">
        <input
          type="checkbox"
          className="h-4 w-4 text-[#b82322] rounded border-gray-300 focus:ring-0"
          checked={selectedQuestions.includes(question.id)}
          onChange={() => handleCheckQuestion(question.id)}
        />
      </label>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 truncate" onClick={handleQuestionClick}>
            {question.questionTitle}
          </h3>
          <div className="flex items-center space-x-2">
            {question.syllabusCode && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">{question.syllabusCode}</span>
            )}
            {question.subject && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">{question.subject}</span>
            )}
            {question.yearOfExam && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">{question.yearOfExam}</span>
            )}
          </div>
        </div>
      </div>
      <button
        onClick={handleQuestionClick}
        className="ml-4 px-4 py-2 bg-[#b82322] text-white rounded-lg hover:bg-[#901c1b] !rounded-button whitespace-nowrap"
      >
        View Details
      </button>
    </div>
  )
}

function FilterTable({
  selectedFilter,
  setSelectedFilter,
  results,
  setSelectedButton,
  showFilterDropdown,
  setShowFilterDropdown,
}) {
  const [categories, setCategories] = useState({})
  const [displayFilter, setDisplayFilter] = useState({})
  const categoryKeys = ["examBoard", "syllabusCode", "subject", "yearOfExam", "session"]
  useEffect(() => {
    if (results.length > 0) {
      const tCategories = {}
      categoryKeys.forEach((title) => {
        const values = ["All"]
        results.forEach((item) => {
          if (title in item) {
            values.push(item[title])
          }
        })
        if (values.length > 0) {
          const valuesSet = new Set(values)
          // tCategories[title] = Array.from(valuesSet);
          if (title === "yearOfExam") {
            tCategories[title] = Array.from(valuesSet)
              .filter((year) => typeof year === "number" || !isNaN(Number(year)))
              .sort((a, b) => b - a)
          } else {
            tCategories[title] = Array.from(valuesSet)
          }
        } else {
          console.log(`${title} missed in all objects or has no valid values.`)
        }
      })
      setCategories(tCategories)
    }
  }, [results])

  const rowCounter = 0

  const handleClick = (rowId, category, value) => {
    if (value === "All") {
      const newFilter = { ...selectedFilter }
      delete newFilter[category]
      setSelectedFilter(newFilter)
    } else {
      setSelectedFilter((prevFilter) => {
        const newFilter = { ...prevFilter }
        newFilter[category] = value
        return newFilter
      })
    }

    setDisplayFilter((prevDisplayFilter) => {
      const newDisplayFilter = { ...prevDisplayFilter }
      newDisplayFilter[category] = value
      return newDisplayFilter
    })

    setShowFilterDropdown(null)
    setSelectedButton(rowId)
  }

  const handleDropdownClick = (id) => {
    setShowFilterDropdown((prevShowDropdown) => (prevShowDropdown === id ? null : id))
  }

  return (
    <div className="flex-1 bg-[#fafafa] rounded">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Filters</h2>
        <div className="space-y-4">
          {categoryKeys.map((category) => (
            <div key={category} className="relative">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  {[category[0].toUpperCase(), ...category.slice(1)].join("")}
                </label>
                <button
                  className="flex items-center px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none hover:border-blue-500 bg-gray-100 text-black"
                  onClick={() => handleDropdownClick(category)}
                >
                  {displayFilter[category] || "All"}
                  <i className="fas fa-chevron-down ml-2 text-gray-400"></i>
                </button>
                {showFilterDropdown === category && (
                  <div className="absolute top-full left-0 mt-2 w-full bg-white border border-gray-300 rounded shadow-lg filter-dropdowns">
                    {categories[category]?.map((value, index) => (
                      <button
                        key={index}
                        className="block w-full text-left px-3 py-2 hover:bg-gray-50 text-sm bg-white text-black space-y-0 m-0"
                        onClick={() => handleClick(category, category, value)}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function FilterCategoryRow({ category }) {
  return <div>{[category[0].toUpperCase(), ...category.slice(1)].join("")}</div>
}

function FilterRow({ category, content, rowId, selectedFilter, handleClick }) {
  const isSelected = selectedFilter[category] === content

  return (
    <div
      className={`px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-gray-100 ${
        isSelected ? "bg-blue-100 text-blue-800" : "text-gray-700"
      }`}
      onClick={() => handleClick(rowId, category, content)}
    >
      {content}
    </div>
  )
}

export default function QuestionSearch() {
  const location = useLocation()
  const navigate = useNavigate()

  // Try to load search state from session storage first
  const savedState = loadSearchState()

  // Initialize state from session storage or location state if available
  const [searchText, setSearchText] = useState(savedState?.searchText || location.state?.searchText || "")
  const [results, setResults] = useState(savedState?.results || location.state?.results || [])
  const [currentQuestions, setCurrentQuestions] = useState(
    savedState?.currentQuestions || location.state?.currentQuestions || [],
  )
  const [selectedFilter, setSelectedFilter] = useState(
    savedState?.selectedFilter || location.state?.selectedFilter || {},
  )
  const [currentQuestion, setCurrentQuestion] = useState(null)
  const [currentPage, setCurrentPage] = useState(savedState?.currentPage || location.state?.currentPage || 1)
  const [itemsPerPage, setItemsPerPage] = useState(savedState?.itemsPerPage || location.state?.itemsPerPage || 5)
  const [selectedButton, setSelectedButton] = useState(
    savedState?.selectedButton || location.state?.selectedButton || null,
  )
  const [selectedQuestions, setSelectedQuestions] = useState(
    savedState?.selectedQuestions || location.state?.selectedQuestions || [],
  )
  const [includeSolution, setIncludeSolution] = useState(
    savedState?.includeSolution || location.state?.includeSolution || false,
  )
  const [showRandomModal, setShowRandomModal] = useState(false)
  const [showFilterDropdown, setShowFilterDropdown] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Store current state in window object for access from other components
  useEffect(() => {
    window.currentSearchResults = results
    window.currentQuestions = currentQuestions
    window.selectedFilter = selectedFilter
    window.currentPage = currentPage
    window.itemsPerPage = itemsPerPage
    window.selectedQuestions = selectedQuestions
    window.includeSolution = includeSolution

    // Save state to session storage whenever it changes
    saveSearchState({
      searchText,
      results,
      currentQuestions,
      selectedFilter,
      currentPage,
      itemsPerPage,
      selectedQuestions,
      includeSolution,
    })
  }, [
    searchText,
    results,
    currentQuestions,
    selectedFilter,
    currentPage,
    itemsPerPage,
    selectedQuestions,
    includeSolution,
  ])

  // Load saved search results if available
  useEffect(() => {
    if (
      (location.state?.results && location.state.results.length > 0 && results.length === 0) ||
      (savedState?.results && savedState.results.length > 0 && results.length === 0)
    ) {
      const stateToUse = savedState || location.state
      setResults(stateToUse.results)
      setCurrentQuestions(stateToUse.currentQuestions || [])
      setCurrentPage(stateToUse.currentPage || 1)
      setSelectedFilter(stateToUse.selectedFilter || {})
      setSelectedQuestions(stateToUse.selectedQuestions || [])
    }
  }, [location.state, savedState])

  const handleQuestionSelected = (question) => {
    setCurrentQuestion(question)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFilterDropdown) {
        const dropdowns = document.querySelectorAll(".filter-dropdowns")
        let clickedInside = false
        dropdowns.forEach((dropdown) => {
          if (dropdown.contains(event.target)) {
            clickedInside = true
          }
        })
        if (!clickedInside) {
          setShowFilterDropdown(null)
        }
      }
    }
    document.addEventListener("click", handleClickOutside)
    return () => {
      document.removeEventListener("click", handleClickOutside)
    }
  }, [showFilterDropdown])

  useEffect(() => {
    // Check if we're returning from question details with saved search state
    const savedState = loadSearchState()
    if (savedState?.searchText && savedState?.results?.length > 0) {
      // If we have search text and results in the saved state
      const isReturningFromDetails =
        location.pathname === "/question-search" && document.referrer.includes("/exam-question/")

      if (isReturningFromDetails || (location.state && location.state.returnedFromDetails)) {
        // Set loading to true briefly to show the loading state
        setLoading(true)

        // Update all the state from saved state
        setSearchText(savedState.searchText)
        setResults(savedState.results)
        setCurrentQuestions(savedState.currentQuestions || [])
        setSelectedFilter(savedState.selectedFilter || {})
        setCurrentPage(savedState.currentPage || 1)
        setSelectedQuestions(savedState.selectedQuestions || [])

        // Explicitly trigger the search again with the saved search text
        const triggerSearch = async () => {
          try {
            await QuestionFetcher.fetchQuestionData(savedState.searchText, setResults)
            // Set loading to false after a short delay to ensure rendering completes
            setTimeout(() => {
              setLoading(false)
            }, 300)
          } catch (error) {
            console.error("Failed to re-fetch search results:", error)
            setLoading(false)
          }
        }

        // Small delay before triggering search to ensure state is updated
        setTimeout(() => {
          triggerSearch()
        }, 100)
      }
    }
  }, [location.pathname, location.state])

  // Create a context value to share with other components
  const searchContextValue = {
    searchText,
    results,
    currentQuestions,
    selectedFilter,
    currentPage,
    itemsPerPage,
    selectedButton,
    selectedQuestions,
    includeSolution,
  }

  // Make the context available to child components
  return (
    <SearchStateContext.Provider value={searchContextValue}>
      <FilterableQuestionTable
        onQuestionSelected={handleQuestionSelected}
        searchText={searchText}
        setSearchText={setSearchText}
        results={results}
        setResults={setResults}
        currentQuestions={currentQuestions}
        setCurrentQuestions={setCurrentQuestions}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        selectedButton={selectedButton}
        setSelectedButton={setSelectedButton}
        selectedQuestions={selectedQuestions}
        setSelectedQuestions={setSelectedQuestions}
        includeSolution={includeSolution}
        setIncludeSolution={setIncludeSolution}
        showRandomModal={showRandomModal}
        setShowRandomModal={setShowRandomModal}
        showFilterDropdown={showFilterDropdown}
        setShowFilterDropdown={setShowFilterDropdown}
        loading={loading}
        setLoading={setLoading}
        error={error}
        setError={setError}
      />
    </SearchStateContext.Provider>
  )
}
